import clsx from "clsx";
import {
  ArrowUturnLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

export interface PlanningToolbarProps {
  isHistorical: boolean;
  onToday: () => void;
  settingsBadge?: number;
  onOpenSettings: () => void;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  undoAvailable?: boolean;
  undoSummary?: string | null;
  undoBusy?: boolean;
  onUndo?: () => void;
}

export function PlanningToolbar({
  isHistorical,
  onToday,
  settingsBadge = 0,
  onOpenSettings,
  fullscreen,
  onToggleFullscreen,
  undoAvailable = false,
  undoSummary,
  undoBusy = false,
  onUndo,
}: PlanningToolbarProps) {
  const undoTitle = undoBusy
    ? "Änderung wird rückgängig gemacht"
    : undoAvailable
      ? undoSummary
        ? `Rückgängig: ${undoSummary}`
        : "Letzte Änderung rückgängig machen"
      : "Keine Änderung zum Rückgängigmachen";

  return (
    <div className="mp-toolbar" role="toolbar" aria-label="Montageplanung">
      <div className="mp-toolbar-start">
        {onUndo ? (
          <button
            type="button"
            className="mp-toolbar-btn mp-toolbar-btn--undo"
            onClick={onUndo}
            disabled={!undoAvailable || undoBusy}
            title={undoTitle}
            aria-label={undoTitle}
          >
            <ArrowUturnLeftIcon className="mp-toolbar-btn__icon" aria-hidden />
            <span className="mp-toolbar-btn__label">Zurück</span>
          </button>
        ) : null}
        {!isHistorical ? (
          <button
            type="button"
            className="mp-toolbar-btn mp-toolbar-btn--today"
            onClick={onToday}
            title="Zum heutigen Tag springen"
          >
            <CalendarDaysIcon className="mp-toolbar-btn__icon" aria-hidden />
            <span className="mp-toolbar-btn__label">Heute</span>
          </button>
        ) : onUndo ? null : (
          <span className="mp-toolbar-spacer" />
        )}
      </div>
      <div className="mp-toolbar-end">
        <button
          type="button"
          className="mp-toolbar-btn mp-toolbar-btn--icon mp-toolbar-btn--settings"
          onClick={onOpenSettings}
          title="Planungseinstellungen"
          aria-label="Planungseinstellungen"
        >
          <Cog6ToothIcon className="mp-toolbar-btn__icon" aria-hidden />
          {settingsBadge > 0 ? (
            <span className="mp-toolbar-btn__badge">{settingsBadge}</span>
          ) : null}
        </button>
        {onToggleFullscreen ? (
          <button
            type="button"
            className={clsx(
              "mp-toolbar-btn mp-toolbar-btn--fullscreen",
              fullscreen && "is-active",
            )}
            onClick={onToggleFullscreen}
            title={
              fullscreen
                ? "Planungsmodus beenden (Esc)"
                : "Planung über volle Seite (Esc zum Beenden)"
            }
            aria-label={fullscreen ? "Planungsmodus beenden" : "Planung über volle Seite"}
            aria-pressed={fullscreen}
          >
            {fullscreen ? (
              <ArrowsPointingInIcon className="mp-toolbar-btn__icon" aria-hidden />
            ) : (
              <ArrowsPointingOutIcon className="mp-toolbar-btn__icon" aria-hidden />
            )}
            <span className="mp-toolbar-btn__label">
              {fullscreen ? "Schließen" : "Vollbild"}
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
